import store from "../../../store";
import {todayStr} from "@/utils/dateFormat";
import {minValue, numeric, required} from "vuelidate/lib/validators";

import { CatchingEventAttributes } from "./Catching/catching";
import { Animal } from "../Animals/animal";
import {InitialEventAttributes} from "./Clinical/initial";
import {SurveyEventAttributes} from "./Clinical/survey";
import {ExaminationEventAttributes} from "./Clinical/examination";
import {SterilizationEventAttributes} from "./Clinical/sterilization";
import {VaccinationEventAttributes} from "./Clinical/vaccination";
import {MarkingEventAttributes} from "./Clinical/marking";
import {KeepingEventAttributes} from "./Keeping/keeping";
import {OwnerEventAttributes} from "./Disposal/owner";
import {DeathEventAttributes} from "./Disposal/death";
import {EuthanasiaEventAttributes} from "./Disposal/euthanasia";
import {TransferEventAttributes} from "@/pages/AppPages/Events/Disposal/transfer";
import {ModelObject} from "@/pages/Common/modelObject";

export const EventKind = Object.freeze({
    CATCHING: 1,
    INITIAL: 2,
    SURVEY: 3,
    CLINICAL: 4,
    STERILIZATION: 5,
    VACCINATION: 6,
    MARKING: 7,
    RETURN_TO_OWNER: 8,
    RETURN_TO_HABITAT: 9,
    DEATH: 10,
    EUTHANASIA: 11,
    KEEPING: 12,
    TRANSFER: 13
});

export const EventKindLabel = Object.freeze({
    1: ['Отлов', 'отлову'],
    2: ['Первичный осмотр', 'первичному осмотру'],
    3: ['Освидетельствование', 'освидетельствованию'],
    4: ['Клинический осмотр', 'клиническому осмотру'],
    5: ['Стерилизация', 'стерилизации'],
    6: ['Вакцинация', 'вакцинации'],
    7: ['Маркирование', 'маркированию'],
    8: ['Возврат владельцу', 'возврату владельцу'],
    9: ['Возврат в прежнюю среду', 'возврату в прежнюю среду'],
    10: ['Смерть', 'смерти'],
    11: ['Эвтаназия', 'эвтаназии'],
    12: ['Содержание', 'содержанию'],
    13: ['Передача между контрактами', 'передаче между контрактами']
});

export const eventNeedsInitialMunicipality = function (kind) {
    return kind === EventKind.CATCHING || kind === EventKind.KEEPING
};

export const eventNeedsInitialKeeping = function (kind) {
    return !(kind === EventKind.CATCHING || kind === EventKind.KEEPING)
};


export class BaseEvent extends ModelObject {

    _initOwnProperty(sourceKey, destinationKey = null, defaultValue = null) {
        if (!destinationKey) { destinationKey = sourceKey }
        this[destinationKey] = this._initObjectParams && this._initObjectParams.hasOwnProperty(sourceKey)
            ? this._initObjectParams[sourceKey] : defaultValue;
    }

    constructor(initObjectParams, attributesClass = null) {
        super(initObjectParams)
        this._initObjectParams = initObjectParams ? initObjectParams : {};
        this._attributesClass = attributesClass;
        this.attributes = {};
        if (attributesClass) {
            this.attributes = new attributesClass();
        }
        this.attachments = [];

        let defaultAnimal = new Animal(initObjectParams);

        this._initOwnProperty('id');
        this._initOwnProperty('request');
        this._initOwnProperty('kind');
        this._initOwnProperty('raid', 'raidId');
        this._initOwnProperty('executorContract', null, null);
        this._initOwnProperty('executorContractObject', null, {});
        this._initOwnProperty('executionDate', null, todayStr());
        this._initOwnProperty('executorName', null, store.getters.userFullName);
        this._initOwnProperty('animal', null, defaultAnimal);
        this._initOwnProperty('kindName');
        this._initOwnProperty('addressShortText');

        this.requestId = this.request ? this.request.id : null;
        this.animalId =  this.animal ? this.animal.id : null;
        this.executorOrganizationId = this.executorContract ? this.executorContractObject['contractor_id'] : null;

        this.gallery = [];
    }

    static get modelName() {
        return 'Event'
    }

    static get validations() {
        return {
            kind: { required },
            executionDate: { required },
            executorName: { required },
            executorContract: { required },
            animalId: { required },
            requestId: { required },
            attributes: {},
            attachments: {}
        }
    }

    static get validationFieldLabels() {
        return {
            kind: 'Тип',
            executionDate: 'Дата исполнения',
            executorName: 'Исполнитель',
            executorContract: 'Контракт',
        }
    }

    static get mapping() {
        return {
            executionDate: 'execution_date',
            executorName: 'executor_name',
            executorContract: 'executor_contract',
            animalId: 'animal',
            requestId: 'request'
        }
    }

    static get label() {
        return 'Мероприятие'
    }

    label() {
        return 'Мероприятие'
    }

    static get newLabel() {
        return 'Новое мероприятие'
    }

    objectLabel() {
        return `${this.kindName} №${this.id}`
    }

    static get states() {
        return {
            [EventKind.CATCHING]: [EventKind.INITIAL],
            [EventKind.INITIAL]: [EventKind.KEEPING],
            [EventKind.KEEPING]: [EventKind.SURVEY],
            [EventKind.SURVEY]: [EventKind.CLINICAL],
            [EventKind.CLINICAL]: [EventKind.STERILIZATION],
            [EventKind.STERILIZATION]: [EventKind.VACCINATION],
            [EventKind.VACCINATION]: [EventKind.MARKING],
            [EventKind.MARKING]: [8, 9, 10, 11]
        }
    }

    applyData(data) {
        if (!data || (data && !data['id'])) { return }

        this.id = data['id'];
        this.kind = data['kind'];
        this.kindName = data['kind_name'];
        this.executionDate = data['execution_date'];
        this.executorName = data['executor_name'];
        this.raidId = data['raid'];
        this.authorOrganizationId = data['author_organization_id'];
        this.addressShortText = data['address_short_text'];

        this.executorContractObject = data['executor_contract_object'];
        if (data['executor_contract_object']) {
            this.executorContract = data['executor_contract_object']['id'];
            this.executorOrganizationId = this.executorContractObject['contractor'];
        }

        if (data['request_object']) {
            this.requestId = data['request_object']['id'];
            this.request = data['request_object'];
        }

        if (data.hasOwnProperty('gallery')) {
            this.gallery = data['gallery'];
        }

        if (data.hasOwnProperty('attributes') && data['attributes'] && this.attributes && typeof this.attributes.applyData === 'function') {
            this.attributes.applyData(data['attributes']);
        }
        if (data.hasOwnProperty('animal_object') && data['animal_object']) {
            this.animalId = data['animal_object']['id'];
            this.animal = new Animal();
            this.animal.applyData(data['animal_object']);
        }

    }

    applyAttributes(attributes) {
        if (this.attributes) {
            this.attributes.applyData(attributes);
        }
    }

    getAvatarAbsolutePath() {
        return this.gallery && this.gallery.length ? this.gallery[0]['thumbnail_absolute_path'] : null;
    }


}

function getAttributesClass(eventKind) {
    switch (eventKind) {
        case EventKind.CATCHING:
            return CatchingEventAttributes;
        case EventKind.INITIAL:
            return InitialEventAttributes;
        case EventKind.SURVEY:
            return SurveyEventAttributes;
        case EventKind.CLINICAL:
            return ExaminationEventAttributes;
        case EventKind.STERILIZATION:
            return SterilizationEventAttributes;
        case EventKind.VACCINATION:
            return VaccinationEventAttributes;
        case EventKind.MARKING:
            return MarkingEventAttributes;
        case EventKind.RETURN_TO_HABITAT:
            return;
        case EventKind.RETURN_TO_OWNER:
            return OwnerEventAttributes;
        case EventKind.DEATH:
            return DeathEventAttributes;
        case EventKind.EUTHANASIA:
            return EuthanasiaEventAttributes;
        case EventKind.KEEPING:
            return KeepingEventAttributes;
        case EventKind.TRANSFER:
            return TransferEventAttributes;
        default:
            return null
    }
}

export const eventFactory = function(kind) {
    const attributesClass = getAttributesClass(kind);

    return class extends BaseEvent {
        constructor(initObjectParams) {
            if (!initObjectParams) {
                initObjectParams = {}
            }
            initObjectParams['kind'] = kind;
            super(initObjectParams, attributesClass);
        }
        static get modelName() {
            switch (kind) {
                case EventKind.CATCHING:
                    return 'CatchingEvent';
                case EventKind.INITIAL:
                case EventKind.SURVEY:
                case EventKind.CLINICAL:
                case EventKind.STERILIZATION:
                case EventKind.VACCINATION:
                case EventKind.MARKING:
                    return 'ClinicalEvent';
                case EventKind.RETURN_TO_HABITAT:
                case EventKind.RETURN_TO_OWNER:
                case EventKind.DEATH:
                case EventKind.EUTHANASIA:
                case EventKind.TRANSFER:
                    return 'DisposalEvent';
                case EventKind.KEEPING:
                    return 'KeepingEvent';
                default:
                    return 'Event'
            }
        }
        static get mapping() {
            return BaseEvent.mapping;
        }
        static get validations() {
            return BaseEvent.validations;
        }
        static get validationFieldLabels() {
            return BaseEvent.validationFieldLabels
        }

    }
};
